import React from 'react'
import PropTypes from 'prop-types'
import { Field } from 'redux-form'
import withFieldError from 'components/form/with-error'

// Only require the ids once the user has started configuring a scoreboard, so
// speed thread widgets without a live scoreboard are not blocked from saving.
export const requiredIfConfiguring = (value, allValues) => {
  const config = (allValues && allValues.scoreboardConfig) || {}
  const anySet = config.adsAccountId || config.cardId || config.framePublicId
  if (anySet && !value) return 'Required'
}

export const nonNegative = value =>
  value !== '' && value != null && Number(value) < 0
    ? 'Must be zero or greater'
    : undefined

// A fractional slot is meaningless (it's a carousel index) and would hard-fail
// every tick in the service, so block it on both save paths.
export const integer = value =>
  value !== '' && value != null && !Number.isInteger(Number(value))
    ? 'Must be a whole number'
    : undefined

export const toNumber = value =>
  value === '' || value == null ? value : Number(value)

// Input that surfaces redux-form validation. redux-form drops `meta` on the
// built-in string `input` component, so validation errors never render; a
// dedicated component paired with withFieldError shows them (as configure-info
// does with ExperienceNameWithError).
export const ScoreboardInput = ({
  input,
  meta: { touched, error },
  id,
  type = 'text',
  min,
  step,
  placeholder
}) => (
  <input
    {...input}
    id={id}
    type={type}
    min={min}
    step={step}
    placeholder={placeholder}
    autoComplete='off'
    className={'form-control ' + (touched && error ? 'has-error' : '')}
  />
)

ScoreboardInput.propTypes = {
  input: PropTypes.any,
  meta: PropTypes.any,
  id: PropTypes.string,
  type: PropTypes.string,
  min: PropTypes.string,
  step: PropTypes.string,
  placeholder: PropTypes.string
}

const ScoreboardField = withFieldError(ScoreboardInput)

const ConfigureScoreboard = () => (
  <div className='form-group darkPanel'>
    <div className='col-md-12'>
      <div className='portlet light'>
        <div className='portlet-title'>
          <div className='caption'>
            <span className='caption-subject bold font-green-haze uppercase'>
              <i
                className='glyphicon glyphicon-stats hidden-xs'
                style={{
                  alignSelf: 'flex-start',
                  marginTop: 8,
                  marginRight: 8
                }}
              />
              Live Scoreboard configuration
            </span>
          </div>
        </div>
        <div className='portlet-body br-experience-scoreboard'>
          <div className='form-group row'>
            <div className='col-sm-12'>
              <p className='help-block'>
                Keep a live win total on the X Ads carousel card. Leave blank if
                this experience has no live scoreboard.
              </p>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              Ads account ID
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-ads-account-id'
                name='scoreboardConfig.adsAccountId'
                component={ScoreboardField}
                placeholder='e.g. 18ce54abcde'
                validate={requiredIfConfiguring}
              />
              <span className='help-block'>
                The X Ads account that owns the carousel card (e.g.
                18ce54abcde).
              </span>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              Card ID
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-card-id'
                name='scoreboardConfig.cardId'
                component={ScoreboardField}
                placeholder='The SWIPEABLE_MEDIA card to update'
                validate={requiredIfConfiguring}
              />
              <span className='help-block'>The carousel card to update.</span>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              Frame public ID
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-frame-public-id'
                name='scoreboardConfig.framePublicId'
                component={ScoreboardField}
                placeholder='Cloudinary public id of the blank frame'
                validate={requiredIfConfiguring}
              />
              <span className='help-block'>
                Cloudinary public id of the blank frame the win total is drawn
                onto.
              </span>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              Counter frame slot
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-slot'
                name='scoreboardConfig.slot'
                component={ScoreboardField}
                type='number'
                min='0'
                step='1'
                normalize={toNumber}
                validate={[nonNegative, integer]}
              />
              <span className='help-block'>
                Which carousel image this journey updates: 0 = first image, 1 =
                second.
              </span>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              Poll interval (ms)
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-poll-interval'
                name='scoreboardConfig.pollIntervalMs'
                component={ScoreboardField}
                type='number'
                min='0'
                placeholder='Leave 0 to use the default'
                normalize={toNumber}
                validate={nonNegative}
              />
              <span className='help-block'>
                How often to check for new wins. Leave 0 to use the default.
              </span>
            </div>
          </div>
          <div className='form-group row'>
            <div className='col-sm-12'>
              <h5 className='bold uppercase font-green-haze'>
                Number styling &amp; position
              </h5>
              <p className='help-block'>
                How the win total is drawn on the frame. Leave any field blank
                to use the default.
              </p>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              Text font
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-text-font'
                name='scoreboardConfig.textFont'
                component={ScoreboardField}
                placeholder='e.g. Arial'
              />
              <span className='help-block'>
                Font for the number (a Cloudinary-supported font). Blank uses
                the default.
              </span>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              Text colour
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-text-color'
                name='scoreboardConfig.textColor'
                component={ScoreboardField}
                placeholder='e.g. white or rgb:1d1d1b'
              />
              <span className='help-block'>Colour of the number.</span>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              Text size (px)
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-text-size'
                name='scoreboardConfig.textSize'
                component={ScoreboardField}
                type='number'
                min='0'
                normalize={toNumber}
                validate={nonNegative}
              />
              <span className='help-block'>
                Font size, scaled to the frame.
              </span>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              Text gravity
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-text-gravity'
                name='scoreboardConfig.textGravity'
                component={ScoreboardField}
                placeholder='e.g. center, south, north'
              />
              <span className='help-block'>
                Anchor position on the frame; combine with the offsets below.
              </span>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              X offset
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-text-x'
                name='scoreboardConfig.textX'
                component={ScoreboardField}
                type='number'
                normalize={toNumber}
              />
              <span className='help-block'>
                Horizontal nudge from the anchor, in px (can be negative).
              </span>
            </div>
          </div>
          <div className='form-group row'>
            <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
              Y offset
            </label>
            <div className='col-sm-9 col-md-9 col-lg-10'>
              <Field
                id='brt-scoreboard-text-y'
                name='scoreboardConfig.textY'
                component={ScoreboardField}
                type='number'
                normalize={toNumber}
              />
              <span className='help-block'>
                Vertical nudge from the anchor, in px (can be negative).
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)

export default ConfigureScoreboard



// WEBPACK FOOTER //
// ./src/pages/widgets/form/configure-scoreboard/index.js